// FacePile.tsx
import { CSSProperties, FC, ReactNode } from 'react'

//
// Types & Interfaces
//

export interface Face {
  id?: string | number
  imageUrl: string
}

interface RenderFacePileResult {
  facesToRender: Face[]
  overflow: number
}

export interface FacePileProps {
  faces: Face[]
  numFaces?: number
  circleSize?: number // in pixels
  offset?: number // how much circles overlap (multiplier of circleSize)
  hideOverflow?: boolean
  containerStyle?: CSSProperties
  circleStyle?: CSSProperties
  imageStyle?: CSSProperties
  overflowStyle?: CSSProperties
  overflowLabelStyle?: CSSProperties
  render?: (opts: { faces: Face[]; numFaces: number }) => ReactNode
}

//
// Default Props
//

const DEFAULT_CIRCLE_SIZE = 20
const DEFAULT_NUM_FACES = 4
const DEFAULT_OFFSET = 1

//
// Utility: compute which faces to render + overflow count
//

function renderFacePile(faces: Face[] = [], numFaces: number): RenderFacePileResult {
  // Reverse so that first item in the array shows up on top (rightmost).
  const entities = [...faces].reverse()

  if (entities.length === 0) {
    return { facesToRender: [], overflow: 0 }
  }

  // Only keep those with valid imageUrl
  const facesWithImageUrls = entities.filter((e) => e.imageUrl && e.imageUrl.length > 0)
  if (facesWithImageUrls.length === 0) {
    return { facesToRender: [], overflow: 0 }
  }

  const facesToRender = facesWithImageUrls.slice(0, numFaces)
  const overflow = entities.length - facesToRender.length

  return { facesToRender, overflow }
}

//
// Circle sub-component
//

interface CircleProps {
  face: Face
  circleSize: number
  offset: number
  imageStyle?: CSSProperties
  circleStyle?: CSSProperties
}

const Circle: FC<CircleProps> = ({ face, circleSize, offset, imageStyle, circleStyle }) => {
  const innerCircleSize = circleSize * 2 // full width/height of the <img>
  const marginRight = circleSize * offset // we’ll apply negative margin on the wrapper

  return (
    <div
      style={{
        marginRight: -marginRight,
        display: 'inline-block',
        ...(circleStyle || {}),
      }}
    >
      <img
        src={face.imageUrl}
        alt=""
        style={{
          width: innerCircleSize,
          height: innerCircleSize,
          borderRadius: circleSize,
          border: '2px solid white',
          objectFit: 'cover',
          ...(imageStyle || {}),
        }}
      />
    </div>
  )
}

//
// Overflow “+N” circle
//

interface OverflowCircleProps {
  overflow: number
  circleSize: number
  offset: number
  circleStyle?: CSSProperties
  overflowStyle?: CSSProperties
  overflowLabelStyle?: CSSProperties
}

const OverflowCircle: FC<OverflowCircleProps> = ({ overflow, circleSize, offset, circleStyle, overflowStyle }) => {
  // We multiply circleSize by 1.8 to make the overflow badge a little smaller than two full circles
  const innerCircleSize = circleSize * 1.8
  // marginLeft between the overflow circle and the next face circle
  const marginLeft = circleSize * offset - circleSize / 1.6

  return (
    <div
      style={{
        display: 'inline-block',
        marginLeft: marginLeft,
        ...(circleStyle || {}),
      }}
    >
      <div
        style={{
          width: innerCircleSize,
          height: innerCircleSize,
          borderRadius: circleSize,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',

          ...(overflowStyle || {}),
        }}
        className="bg-newCreativeOSGradient text-white mt-1"
      >
        +{overflow}
      </div>
    </div>
  )
}

//
// FacePile Component
//

const FacePile: FC<FacePileProps> = (props) => {
  const {
    faces,
    numFaces = DEFAULT_NUM_FACES,
    circleSize = DEFAULT_CIRCLE_SIZE,
    offset = DEFAULT_OFFSET,
    hideOverflow = false,
    containerStyle,
    circleStyle,
    imageStyle,
    overflowStyle,
    overflowLabelStyle,
    render,
  } = props

  // If user provided a custom render prop, defer entirely to that
  if (render) {
    return <>{render({ faces, numFaces })}</>
  }

  // Otherwise, use our default face-pile logic
  const { facesToRender, overflow } = renderFacePile(faces, numFaces)

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'row-reverse',
        flexWrap: 'nowrap',
        ...(containerStyle || {}),
      }}
      className="my-4"
    >
      {overflow > 0 && !hideOverflow && (
        <OverflowCircle
          overflow={overflow}
          circleSize={circleSize}
          offset={offset}
          circleStyle={circleStyle}
          overflowStyle={overflowStyle}
          overflowLabelStyle={overflowLabelStyle}
        />
      )}

      {Array.isArray(facesToRender) &&
        facesToRender.map((face, idx) => {
          // If face.imageUrl is missing, skip
          if (!face || !face.imageUrl) return null
          return (
            <Circle
              key={face.id ?? idx}
              face={face}
              circleSize={circleSize}
              offset={offset}
              imageStyle={imageStyle}
              circleStyle={circleStyle}
            />
          )
        })}
    </div>
  )
}

export default FacePile
